<template>
  <Head>
    <meta http-equiv="X-UA-Compatible" content="IE=edge">
    <meta name="apple-mobile-web-app-capable" content="yes">
    <meta name="mobile-web-app-capable" content="yes">
    <meta name="twitter:card" content="summary_large_image">
    <!-- <meta name="twitter:site" content="@TODO_CHANGEME">
    <meta name="twitter:creator" content="@TODO_CHANGEME"> -->
    <meta property="og:type" content="website">
    <meta property="og:image:type" content="image/jpeg">
    <meta property="og:image:width" content="1200">
    <meta property="og:image:height" content="675">
  </Head>

  <div class="app-container relative flex flex-auto" :style="{minHeight: `${windowHeight}px`}">
    <router-view v-slot="{ Component, route }">
      <transition mode="out-in">
        <!-- be careful with this key: it will transition all pages with different route names (not full paths) -->
        <component :is="Component" :key="route.meta.transitionKey" class="page flex flex-auto flex-col items-center justify-center" />
      </transition>
    </router-view>

    <!-- version info -->
    <VersionInfo v-if="stage !== 'production'" />
  </div>
</template>

<script>
import { inject, provide, computed, reactive, defineComponent } from 'vue'
import { useRoute } from 'vue-router'
import { Head, useHead } from '@vueuse/head'
import { useWindowSize } from '@vueuse/core'
import { getGlobalFeatures, useI18nInjection } from '@monogrid/vue3-boost/index'
import { VersionInfo } from '@monogrid/vue3-boost/components'
import Spinner from 'vue-easy-spinner/package/Spinner.vue'

import { useWebSocket } from '@/functions/useWebSocket'

import {
  useModernizr,
  usePlatform,
  useModernizrStyleClasses,
  useSeo
} from '@monogrid/vue3-boost/functions'

export default defineComponent({
  name: 'App',
  components: {
    Head,
    Spinner,
    VersionInfo
  },
  /**
   * Introduction
   *
   * https://v3.vuejs.org/guide/composition-api-introduction.html#basics-of-composition-api
   *
   * Setup functions
   * https://v3.vuejs.org/guide/composition-api-setup.html
   */
  setup (props, { attrs, slots, emit }) {
    const { setSeoDefaults, getReactiveSeo } = useSeo()
    const seo = getReactiveSeo()
    // https://next.router.vuejs.org/guide/advanced/composition-api.html
    const route = useRoute()

    const { USE_I18N } = getGlobalFeatures()
    let i18n
    if (USE_I18N) {
      // https://vue-i18n.intlify.dev/guide/advanced/composition.html#basic-usage
      i18n = inject(useI18nInjection)()
    }

    const testClasses = useModernizrStyleClasses({ ...useModernizr(), ...usePlatform() })

    const ws = useWebSocket()
    provide('ws', ws)

    setSeoDefaults({
      siteTitle: 'SCHWAN',
      description: '',
      image: ''
    })

    useHead({
      title: seo.title,
      htmlAttrs: USE_I18N
        ? reactive({ lang: i18n.locale, class: testClasses })
        : reactive({ lang: 'en', class: testClasses }),
      meta: [
        { key: 'ogtitle', property: 'og:title', itemprop: 'name', content: seo.title },
        { key: 'description', name: 'description', content: seo.description },
        { key: 'ogdescription', property: 'og:description', content: seo.description },
        { key: 'ogimage', property: 'og:image', content: seo.image },
        { key: 'ogurl', property: 'og:url', content: computed(() => `https://${SERVICE_TARGET_DOMAIN}${route.fullPath}`) }
      ]
    })

    const { height: windowHeight } = useWindowSize()
    return {
      stage: SERVICE_STAGE,
      windowHeight
    }
  }
})
</script>

<style lang="scss">
@font-face {
  font-display: fallback;
  font-family: NeuzeitGro;
  font-weight: 700;
  src:
    url("@/assets/fonts/NeuzeitGro-Bol.woff2") format("woff2"),
    url("@/assets/fonts/NeuzeitGro-Bol.woff") format("woff"),
    url("@/assets/fonts/NeuzeitGro-Bol.ttf") format("truetype");
}

@font-face {
  font-display: fallback;
  font-family: NeuzeitGro;
  font-weight: 400;
  src:
    url("@/assets/fonts/NeuzeitGro-Reg.woff2") format("woff2"),
    url("@/assets/fonts/NeuzeitGro-Reg.woff") format("woff"),
    url("@/assets/fonts/NeuzeitGro-Reg.ttf") format("truetype");
}

html,
body {
  font-family: NeuzeitGro, sans-serif;
  line-height: 1.1;
  overscroll-behavior: none;
}

.logo {
  position: fixed;
  top: rem(50px);
  left: 50%;
  width: rem(30px);
  height: rem(30px);
  margin-left: rem(-15px);
}

.h1 {
  font-size: rem(28px);
  line-height: 1;

  @screen md {
    font-size: rem(48px);
  }
}

.h2 {
  font-size: rem(24px);

  @screen md {
    font-size: rem(32px);
  }
}

.t-l {
  font-size: rem(18px);
}

.t-m {
  font-size: rem(16px);
}

.t-s {
  font-size: rem(14px);
}

.t-xs {
  font-size: rem(12px);
}

.c-violet { color: $c-violet; }
.c-green { color: $c-green; }
.c-yellow { color: $c-yellow; }
.c-blue { color: $c-blue; }
.c-orange { color: $c-orange; }
.c-purple { color: $c-purple; }
.c-red { color: $c-red; }

.loading-page-chunk {
  text-align: center;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

</style>
